import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SECTIONS = [
  {
    title: 'The short version',
    body: "Chipless is free and doesn't ask you to create an account or hand over personal details. We collect limited usage data that isn't linked to your real identity, so we can understand how the app is used and make it better. We don't sell your data, and we don't run ads.",
  },
  {
    title: 'Who we are',
    body: 'Chipless is operated by Michael Jonna, who is responsible for how your data is handled. You can reach us at playchipless@outlook.com.',
  },
  {
    title: 'What we collect',
    body: 'We use privacy-first, cookieless analytics to understand how Chipless is used. This includes information such as:',
    list: [
      'Pages you visit and basic actions like starting or finishing a game.',
      'Approximate location (country/region), derived from your IP address — not your precise location.',
      'Device and browser type, and general usage patterns like session length.',
      'How you arrived at the site, so we know which channels people find us through.',
    ],
    after:
      "Traffic is measured with Cloudflare Web Analytics, which uses no cookies. Product usage (like how many hands are dealt) is measured with PostHog, also configured without cookies, keyed to the same anonymous sign-in token described below. A few overall gameplay counts (like total games or hands) are kept in our own database. These usage numbers are not linked to your real identity and are reviewed in aggregate. Separately, your browser keeps a small profile that isn't linked to your real identity so the app can recognize you when you return — see \u201cYour anonymous profile\u201d below.",
  },
  {
    title: 'Your anonymous profile',
    body: "So returning players don't have to retype their name and can see their own play history, we store a tiny profile tied to the anonymous sign-in token your browser already uses. It holds only the display name you enter, counts of how many games and hands you've played, when your profile was first created, and timestamps of when you host, join, or rejoin a room. It is not linked to your real identity, email, phone, or precise location, and we don't use it for advertising or sell it. It persists across visits as long as your browser keeps the anonymous token; clearing your browser storage starts you over as a brand-new anonymous user.",
  },
  {
    title: 'Game data',
    body: "When you host or join a game, gameplay details (player names you enter, stacks, and pots) are stored temporarily so every player's phone stays in sync. This is used only to run your game and isn't tied to your identity.",
  },
  {
    title: 'How long we keep data',
    body: "We keep as little as possible. Game rooms — including any names you enter — are automatically deleted a few minutes after the game goes inactive. Analytics data isn't linked to your real identity and is reviewed in aggregate.",
  },
  {
    title: 'Where your data is processed',
    body: 'Chipless runs on Google Firebase and Cloudflare, and uses PostHog for anonymous product analytics. These providers process data on servers in the United States and other countries, under their own privacy and data-protection safeguards.',
  },
  {
    title: 'Why we use your data',
    body: "We process game data and your anonymous profile because it's needed to run the app you asked to use. We process analytics data because we have a legitimate interest in understanding how Chipless is used so we can fix problems and improve it. We don't use your data for advertising or sell it.",
  },
  {
    title: 'Who can use Chipless',
    body: "Chipless is a poker companion intended for adults and isn't directed to children under 13.",
  },
  {
    title: 'Cookies & local storage',
    body: "We don't use tracking cookies, and our analytics are cookieless. An anonymous sign-in token persists in your browser to connect you to our database; it's required to run the app and isn't used for advertising. That same token keys the small anonymous profile described above (your display name and game/hand counts). We also briefly store your current seat so a refresh doesn't kick you out. You can clear all of this through your browser settings — the game still works.",
  },
  {
    title: 'Your choices',
    body: "Our analytics are cookieless and not linked to your real identity. To reset your anonymous profile on your device, clear your browser's local storage for this site. To have your profile and any related data deleted from our servers too, email us (see Your rights below). None of this affects your ability to play.",
  },
  {
    title: 'Your rights',
    body: "Depending on where you live (including the EU, UK, and some US states), you may have the right to access, correct, delete, or get a copy of your data, and to object to or restrict how we use it. To make a request, email playchipless@outlook.com and tell us the display name you use and roughly when you last played so we can find your profile. We'll respond within 30 days. If you're in the EU or UK, you can also complain to your local data protection authority.",
  },
  {
    title: 'Questions',
    body: 'Reach out any time:',
    email: 'playchipless@outlook.com',
  },
];

function Privacy() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Privacy – Chipless</title>
        <meta name="description" content="How Chipless handles your data. We use privacy-friendly, cookieless analytics, don't require an account, and never sell your information." />
        <link rel="canonical" href="https://playchipless.com/privacy" />
      </Helmet>

      <div className="min-h-[100dvh] bg-cream text-ink flex flex-col">
        {/* Top bar */}
        <header className="flex items-center px-5 pt-5 pb-2 sm:px-8 sm:pt-7">
          <button
            onClick={() => navigate('/')}
            className="p-1.5 -ml-1.5 text-mute hover:text-ink transition-colors"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </header>

        <main className="flex-1 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-8 pb-16">
          <h1 className="text-[32px] sm:text-[40px] leading-[1.1] tracking-[-0.02em] font-semibold text-ink">
            Privacy
          </h1>
          <p className="mt-2 text-[15px] text-mute leading-snug">Last updated September 2026</p>

          <div className="mt-10 divide-y divide-line-2">
            {SECTIONS.map((section) => (
              <div key={section.title} className="py-6">
                <h2 className="text-[17px] font-semibold text-ink">{section.title}</h2>
                <p className="mt-2 text-[15px] text-ink-2 leading-relaxed">{section.body}</p>
                {section.list && (
                  <ul className="mt-3 list-disc list-outside pl-5 space-y-1.5 text-[15px] text-ink-2 leading-relaxed">
                    {section.list.map((li) => (
                      <li key={li}>{li}</li>
                    ))}
                  </ul>
                )}
                {section.after && (
                  <p className="mt-3 text-[15px] text-ink-2 leading-relaxed">{section.after}</p>
                )}
                {section.email && (
                  <a
                    href={`mailto:${section.email}`}
                    className="mt-1 inline-block text-[15px] text-ink font-medium underline underline-offset-4 decoration-line hover:decoration-ink transition-colors break-all"
                  >
                    {section.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        </main>
      </div>
    </>
  );
}

export default Privacy;
